import { matchResourceTiming, recordCurrentJourneyCall, shortenResourceName } from '@common/metrics/journey/core';
import { captureException } from '@common/sentry';
import { SentryTags } from '@common/sentry/SentryTags';
import { ProphecyError } from '@prophecy/utils/error';

import { authorizedFetch, authorizedFetchWithoutShareTokens } from './autorizedFetch';

// Resolved to absolute: PerformanceResourceTiming.name is always absolute, so matchResourceTiming's
// exact-string match would silently never hit for a relative request URL like '/api/projects'.
function resolvedUrlString(url: RequestInfo): string {
  const rawUrl = typeof url === 'string' ? url : url.url;
  return new URL(rawUrl, window.location.origin).href;
}

/** Reports one REST round-trip to whichever page/action journey is current — see `recordCurrentJourneyCall`. */
function trackRestCall(url: RequestInfo, startedAt: number): void {
  // Called from `finally`: a throw here must never win over the call's own return value or
  // error — an exception raised inside `finally` replaces whatever the `try` block was doing.
  try {
    const endedAt = performance.now();
    const resolvedUrl = resolvedUrlString(url);
    const timing = matchResourceTiming(resolvedUrl, startedAt, endedAt);
    recordCurrentJourneyCall(shortenResourceName(resolvedUrl), endedAt - startedAt, {
      kind: 'rest',
      startedAt,
      endedAt,
      networkMs: timing?.networkMs,
      resourceDurationMs: timing?.resourceDurationMs
    });
  } catch (error) {
    captureException({
      exception: error instanceof Error ? error : new ProphecyError(String(error)),
      errorTags: { [SentryTags.ProphecyErrorHttpType]: 'http.trackRestCall' }
    });
  }
}

// clone first: reading the body consumes it, so the text fallback needs its own copy
async function readBody(response: Response) {
  const clone = response.clone();
  try {
    return await response.json();
  } catch {
    // a connection dropped mid-body fails the text read as well, and this one has no fallback left
    return await clone.text().catch(() => '');
  }
}

export async function makeRawRestCall(url: RequestInfo, requestConfig?: RequestInit) {
  const startedAt = performance.now();
  try {
    const response = await authorizedFetch(url, requestConfig);
    return response;
  } finally {
    trackRestCall(url, startedAt);
  }
}
export async function makeRestCall(url: RequestInfo, requestConfig?: RequestInit) {
  const startedAt = performance.now();
  try {
    const response = await authorizedFetch(url, requestConfig);

    return await readBody(response);
  } finally {
    trackRestCall(url, startedAt);
  }
}

export async function makeRestCallWithoutShareTokens(url: RequestInfo, requestConfig?: RequestInit) {
  const startedAt = performance.now();
  try {
    const response = await authorizedFetchWithoutShareTokens(url, requestConfig);

    return await readBody(response);
  } finally {
    trackRestCall(url, startedAt);
  }
}
